import { type PropsWithChildren, useMemo } from "react"
import { useSafeContext } from "@oefr/hooks"
import { type CookieValueTypes } from "cookies-next"
import { hasPortfolio } from "@/entities/accountUser"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { createNamedContext } from "@/utils/create-named-context"
import { useBreadcrumbs } from "../hooks/useBreadcrumbs"
import { usePreviousPage } from "../hooks/usePreviousPage"
import { type Breadcrumb } from "../types/Breadcrumb"
import { type TransformBreadcrumb } from "../types/TransformBreadcrumb"

const graphQLFragments = {
    accountUser: graphql(
        `
            fragment DashboardLayoutBreadcrumbContext_AccountUser on AccountUserType {
                ...hasPortfolio_AccountUser
            }
        `,
        [hasPortfolio.fragments.accountUser]
    ),
}

type DashboardLayoutContextValue = {
    breadcrumbs: Breadcrumb[]
    previousPage: Breadcrumb
}

const DashboardLayoutBreadcrumbContext =
    createNamedContext<DashboardLayoutContextValue>("DashboardLayoutContext")

type DashboardLayoutBreadcrumbContextProviderProps = PropsWithChildren<{
    user: FragmentOf<typeof graphQLFragments.accountUser>
    transformBreadcrumb?: TransformBreadcrumb
    lastDashboardNavigationCookie?: CookieValueTypes
}>

export function DashboardLayoutBreadcrumbContextProvider({
    children,
    ...props
}: DashboardLayoutBreadcrumbContextProviderProps) {
    const user = readFragment(graphQLFragments.accountUser, props.user)

    const breadcrumbs = useBreadcrumbs({ hasPortfolio: hasPortfolio(user), ...props })
    const previousPage = usePreviousPage(breadcrumbs)

    const value = useMemo(() => ({ breadcrumbs, previousPage }), [breadcrumbs, previousPage])

    return (
        <DashboardLayoutBreadcrumbContext.Provider value={value}>
            {children}
        </DashboardLayoutBreadcrumbContext.Provider>
    )
}

export function useDashboardLayoutBreadcrumbContext() {
    return useSafeContext(DashboardLayoutBreadcrumbContext)
}

DashboardLayoutBreadcrumbContextProvider.messages = [...useBreadcrumbs.messages] as const
DashboardLayoutBreadcrumbContextProvider.fragments = graphQLFragments
